<i18n>
{
  "en": {
    "clausesList": "Clauses list",
    "confirm":  "Confirm",
    "createItem": "Create grid item",
    "createItemSubtitle":  "Create grid item to display",
    "editItem": "Edit grid item",
    "emptyFieldError": "Required field",
    "freeText": "Free text",
    "fileFormatError": "Invalid format",
    "fileSizeError": "Image is too big. Maximum size is 2 MB.",
    "image": "Image",
    "imageHint": "Image to show in grid",
    "imageHintExisting": "Image to show in grid. (the current image will be overridden)",
    "imageType": "Image",
    "itemName":  "Grid item name",
    "itemText":  "Free text",
    "menuSection": "Menu section",
    "notSpacesAllowed": "Item name must be without spaces",
    "social": "Social",
    "tooLongText": "Input text is too long",
    "type": "Grid item type"
  },
  "es": {
    "clausesList": "Lista de las cláusulas",
    "confirm":  "Confirmar",
    "createItem": "Crear elemento de la plantilla",
    "createItemSubtitle":  "Elemento que será mostrado en la plantilla, en la posición seleccionada",
    "editItem": "Editar elemento de la plantilla",
    "emptyFieldError": "Campo obligatorio",
    "fileFormatError": "Formato no válido",
    "fileSizeError": "La imagen es demasiado grande. El tamaño máximo es 2 MB.",
    "freeText": "Texto libre",
    "image": "Imagen",
    "imageHint": "Imagen a mostrar en la plantilla",
    "imageHintExisting": "Imagen de fondo de la plantilla (la actual será sobreescrita)",
    "imageType": "Imagen",
    "itemName":  "Nombre del elemento",
    "itemText":  "Texto libre",
    "menuSection": "Sección del menú",
    "notSpacesAllowed": "El nombre del elemento deber ir sin espacios",
    "social": "Redes sociales",
    "tooLongText": "El texto es demasiado largo",
    "type": "Tipo de elemento"
  }
}
</i18n>

<template>
  <v-dialog @click:outside="onCancel" v-model="visible" max-width="600px" scrollable persistent>
    <v-card v-if="visible" height="100%" class="px-5 py-4">
      <div class="d-flex flex-column">
        <v-card-title class="word-break pa-0 mb-4">
          {{ !editMode ? $t('createItem') : $t('editItem') }}
        </v-card-title>
      </div>
      <v-container class="pa-0">
        <v-form v-model="valid" ref="formModal">
          <v-text-field
            v-model="name"
            :label="$t('itemName')"
            class="required"
            :rules="[noSpacesRule]"
          />
          <v-select
            v-model="type"
            :items="optionsType"
            :label="$t('type')"
            item-value="value"
            item-text="name"
            class="required"
            chips
            :rules="objectRules"
          />
          <DynameatLangugageFieldGenerator
            v-model="text"
            :label="$t('itemText')"
            :type="'textArea'"
            :rules="shortTextRulesOrEmpty"
            v-if="showField(4)"
          />
          <v-file-input
            v-model="image"
            :show-size="showImageSize"
            :rules="imageRules"
            accept="image/png, image/jpeg, image/bmp"
            prepend-inner-icon="mdi-camera"
            prepend-icon=""
            persistent-hint
            :hint="imageHint"
            :label="$t('image')"
            class="mb-6"
            v-if="showField(3)"
            required
          />
          <v-select
            v-model="clauseSelected"
            :items="clausesList"
            :label="$t('clausesList')"
            item-value="guid"
            item-text="title_alt1"
            class="required"
            chips
            :rules="objectRules"
            v-if="showField(2)"
          />
          <dynameat-modal-buttons
            @cancel="onCancel"
            @confirm="onConfirm"
            :confirmDisabled="!isValid"
            :confirmText="$t('confirm')"
          />
        </v-form>
      </v-container>
    </v-card>
  </v-dialog>
</template>

<script>

import Vue from 'vue';
import Component from 'vue-class-component';
import { Components, MapActions, MapGetters, } from '@core/helpers/decorators';
import { Prop, Watch } from 'vue-property-decorator';
import DynameatLangugageFieldGenerator from '@dynameat/components/DynameatLanguageFieldGenerator';
import DynameatModalButtons from '@dynameat/components/DynameatModalButtons';
import {
  imageFileType, maxChars, maxSize, notNull
} from '@core/helpers/formValidators';
import { MenuGridItem } from '../models';

@Component
@Components({
  DynameatLangugageFieldGenerator,
  DynameatModalButtons
})
@MapGetters(['clausesList', 'pointOfSaleLanguages', 'gridConfiguration'])
@MapActions(['clausesRequest'])
export default class GridItemCreateEditModal extends Vue {
  @Prop({ Boolean, default: false }) editMode;

  @Prop({ Boolean, default: false }) visible;

  @Prop({
    Object,
    validator(item) {
      return item instanceof MenuGridItem;
    },
  }) menuGridItem;

  created() {
    this.clausesRequest();
  }

  valid = false;

  name = ''

  type = null;

  text = {}

  image = null

  clauseSelected = null

  noSpacesRule(value) {
    // Use regex to check for any whitespace character
    return !/\s/.test(value) || this.$t('notSpacesAllowed');
  }

  get optionsType() {
    return [
      { name: this.$t('menuSection'), value: 2 },
      { name: this.$t('image'), value: 3 },
      { name: this.$t('freeText'), value: 4 },
      { name: this.$t('social'), value: 1 },
    ];
  }

  get showImageSize() {
    return this.image instanceof File && this.image.size > 0;
  }

  get imageHint() {
    if (this.image) {
      return this.$t('imageHintExisting');
    }
    return this.$t('imageHint');
  }

  get objectRules() {
    return [notNull(this.$t('emptyFieldError'))];
  }

  get shortTextRulesOrEmpty() {
    return {
      alt1: [maxChars(this.$t('tooLongText'), 1024)],
      alt2: [maxChars(this.$t('tooLongText'), 1024)],
      alt3: [[maxChars(this.$t('tooLongText'), 1024)]]
    };
  }

  get imageRules() {
    return [maxSize(this.$t('fileSizeError'), 2000000), imageFileType(this.$t('fileFormatError'))];
  }

  get isValid() {
    return (this.name && this.type)
      && (
        (this.type === 2 && this.clauseSelected)
        || (this.type === 3 && this.image)
        || (this.type === 4 && this.text)
        || (this.type === 1)
      );
  }

  get availableLanguages() {
    return this.pointOfSaleLanguages;
  }

  showField(type) {
    return this.type === type;
  }

  clearOwnData() {
    this.image = null;
    this.name = '';
    this.type = null;
    this.text = {};
    this.clauseSelected = null;
  }

  onCancel() {
    this.$emit('cancel');
    this.clearOwnData();
  }

  capitalized(text) {
    return text.charAt(0).toUpperCase() + text.slice(1);
  }

  applyGridItemChanges() {
    this.availableLanguages.forEach((el) => {
      this.menuGridItem[`text${this.capitalized(el.alternative)}`] = this.text[el.alternative];
    });
    this.menuGridItem.type = this.type;
    this.menuGridItem.image = this.image;
    this.menuGridItem.name = this.name;
    this.menuGridItem.menuSection = this.clauseSelected;
    this.menuGridItem.menuGridConfiguration = this.gridConfiguration.guid;
  }

  loadGridItemChanges(updatedData) {
    this.type = updatedData.type;
    this.image = updatedData.image;
    this.name = updatedData.name;
    this.clauseSelected = updatedData.menuSection;
    this.availableLanguages.forEach((el) => {
      this.text[el.alternative] = updatedData[`text${this.capitalized(el.alternative)}`];
    });
  }

  onConfirm() {
    if (this.$refs.formModal.validate()) {
      this.applyGridItemChanges();
      this.$emit('confirm', this.menuGridItem);
    }
  }

  @Watch('menuGridItem', { immediate: true })
  onMenuGridItemChange(updatedData) {
    if (updatedData) {
      this.loadGridItemChanges(updatedData);
    }
  }
}
</script>
